/**
 * Airports, terminals and car parks - a small local index searched before the
 * geocoder ever sees the query.
 *
 * WHY THIS EXISTS. Mapbox ranks an airport below any business that happens to
 * carry its name, and for the two Bangkok airports it is worse than that.
 * Checked against the live API:
 *   "don mueang"          -> a locality, three hotels, a temple, a market.
 *                            Thailand's second-busiest airport: not in the top 8.
 *   "suvarnabhumi"        -> the airport rail station only. Not the airport.
 *   "suvarnabhumi airport"-> the airport three times over, then hotels.
 *   "suvarnabhumi parking"-> nothing at all.
 * A driver taking someone to a flight is the least able to sit there refining a
 * query, so airports are the one category worth knowing by heart.
 *
 * WHERE THE DATA CAME FROM. Every coordinate here was taken from OpenStreetMap
 * (Overpass, aeroway=aerodrome/terminal and amenity=parking), not estimated -
 * these are gates a truck can actually be driven to. Mapbox's own maps are
 * largely built from the same OSM data; what differs is the search index, which
 * simply does not return terminals or car parks. So this is not a second map,
 * it is the handful of facts the index drops, carried in the bundle - no extra
 * request, and it works with no signal at the roadside.
 *
 * The list is Thailand-only on purpose: it is the country the app is for, and
 * 44 airports is a few kilobytes.
 */

import type { LngLat, Place } from '../core/types';
import { distance } from '../core/geo';
import { getLang } from '../core/i18n';

export interface AirportFeature {
  /** Stable id suffix, e.g. 't1', 'zone2'. */
  key: string;
  name: string;
  nameTh: string;
  center: LngLat;
  kind: 'terminal' | 'parking';
  /** Floors this structure has, when it is a multi-storey car park. */
  levels?: number;
  /** Extra words that should find it ("departures", "ผู้โดยสารขาออก"). */
  alias?: string[];
  /** One line of orientation shown under the name. */
  note?: string;
  noteTh?: string;
}

export interface Airport {
  iata: string;
  name: string;
  nameTh: string;
  center: LngLat;
  alias?: string[];
  features?: AirportFeature[];
}

const a = (
  iata: string,
  name: string,
  nameTh: string,
  lng: number,
  lat: number,
  alias?: string[],
  features?: AirportFeature[],
): Airport => ({ iata, name, nameTh, center: [lng, lat], alias, features });

/*
 * Suvarnabhumi's landside detail.
 *
 * Zones 2 and 3 are the multi-storey garages beside the terminal - six floors
 * each, per OSM's building:levels - which is what a driver means by "car park
 * floor 5". Zones 4-7 are surface lots. The satellite concourse (SAT-1) is
 * deliberately NOT here: it is airside, reached by the automated train from
 * inside the terminal, so offering to drive to it would be sending someone to a
 * place no road reaches.
 */
const BKK_FEATURES: AirportFeature[] = [
  {
    key: 'terminal',
    name: 'Main Passenger Terminal',
    nameTh: 'อาคารผู้โดยสารหลัก',
    center: [100.75052, 13.69134],
    kind: 'terminal',
    alias: ['terminal', 'terminal 1', 't1', 'departures', 'arrivals', 'ผู้โดยสาร', 'ขาออก', 'ขาเข้า'],
    note: 'Departures level 4 · Arrivals level 2',
    noteTh: 'ผู้โดยสารขาออก ชั้น 4 · ขาเข้า ชั้น 2',
  },
  {
    key: 'zone2',
    name: 'Car Park Zone 2',
    nameTh: 'อาคารจอดรถ โซน 2',
    center: [100.7522, 13.69411],
    kind: 'parking',
    levels: 6,
    alias: ['car park', 'carpark', 'parking', 'garage', 'จอดรถ', 'ที่จอดรถ'],
    note: 'Multi-storey, floors 1-6',
    noteTh: 'อาคารจอดรถ ชั้น 1-6',
  },
  {
    key: 'zone3',
    name: 'Car Park Zone 3',
    nameTh: 'อาคารจอดรถ โซน 3',
    center: [100.75044, 13.69459],
    kind: 'parking',
    levels: 6,
    alias: ['car park', 'carpark', 'parking', 'garage', 'จอดรถ', 'ที่จอดรถ'],
    note: 'Multi-storey, floors 1-6',
    noteTh: 'อาคารจอดรถ ชั้น 1-6',
  },
  {
    key: 'zone4',
    name: 'Car Park Zone 4',
    nameTh: 'ลานจอดรถ โซน 4',
    center: [100.7493, 13.69484],
    kind: 'parking',
    alias: ['car park', 'carpark', 'parking', 'จอดรถ'],
    note: 'Surface lot',
    noteTh: 'ลานจอดรถ',
  },
  {
    key: 'zone5',
    name: 'Car Park Zone 5',
    nameTh: 'ลานจอดรถ โซน 5',
    center: [100.75054, 13.69661],
    kind: 'parking',
    alias: ['car park', 'carpark', 'parking', 'จอดรถ'],
    note: 'Surface lot',
    noteTh: 'ลานจอดรถ',
  },
  {
    key: 'zone6',
    name: 'Car Park Zone 6',
    nameTh: 'ลานจอดรถ โซน 6',
    center: [100.75161, 13.69883],
    kind: 'parking',
    alias: ['car park', 'carpark', 'parking', 'จอดรถ'],
    note: 'Surface lot',
    noteTh: 'ลานจอดรถ',
  },
  {
    key: 'zone7',
    name: 'Car Park Zone 7',
    nameTh: 'ลานจอดรถ โซน 7',
    center: [100.75304, 13.69846],
    kind: 'parking',
    alias: ['car park', 'carpark', 'parking', 'จอดรถ'],
    note: 'Surface lot',
    noteTh: 'ลานจอดรถ',
  },
  {
    key: 'longterm',
    name: 'Long-term Car Park',
    nameTh: 'ลานจอดรถระยะยาว',
    center: [100.7678, 13.70653],
    kind: 'parking',
    alias: ['long term', 'longterm', 'long stay', 'จอดรถระยะยาว'],
    note: 'Cheapest, shuttle to the terminal',
    noteTh: 'ราคาถูกที่สุด มีรถรับส่งไปอาคารผู้โดยสาร',
  },
];

/** Don Mueang: two passenger terminals, and which is which is the whole question. */
const DMK_FEATURES: AirportFeature[] = [
  {
    key: 't1',
    name: 'Terminal 1',
    nameTh: 'อาคารผู้โดยสาร 1',
    center: [100.60371, 13.9211],
    kind: 'terminal',
    alias: ['terminal 1', 't1', 'international', 'ระหว่างประเทศ', 'ผู้โดยสาร'],
    note: 'International flights',
    noteTh: 'เที่ยวบินระหว่างประเทศ',
  },
  {
    key: 't2',
    name: 'Terminal 2',
    nameTh: 'อาคารผู้โดยสาร 2',
    center: [100.60187, 13.91747],
    kind: 'terminal',
    alias: ['terminal 2', 't2', 'domestic', 'ในประเทศ', 'ผู้โดยสาร'],
    note: 'Domestic flights',
    noteTh: 'เที่ยวบินในประเทศ',
  },
];

/** Coordinates from OpenStreetMap; names as OSM has them, aliases added by hand. */
export const AIRPORTS: Airport[] = [
  a('BKK', 'Suvarnabhumi Airport', 'ท่าอากาศยานสุวรรณภูมิ', 100.75232, 13.68167,
    ['suvarnabhumi', 'suwannaphum', 'suvarnbhumi', 'swampy', 'bangkok airport', 'สุวรรณภูมิ'],
    BKK_FEATURES),
  a('DMK', 'Don Mueang International Airport', 'ท่าอากาศยานดอนเมือง', 100.60406, 13.91264,
    ['don mueang', 'don muang', 'donmuang', 'donmueang', 'ดอนเมือง'], DMK_FEATURES),
  a('CNX', 'Chiang Mai International Airport', 'ท่าอากาศยานนานาชาติเชียงใหม่', 98.9647, 18.76651, ['chiang mai', 'chiangmai', 'เชียงใหม่']),
  a('HKT', 'Phuket International Airport', 'ท่าอากาศยานนานาชาติภูเก็ต', 98.31775, 8.11001, ['phuket', 'ภูเก็ต']),
  a('USM', 'Samui International Airport', 'ท่าอากาศยานนานาชาติสมุย', 100.06259, 9.54881, ['samui', 'koh samui', 'สมุย']),
  a('UTP', 'U-Tapao Pattaya International Airport', 'ท่าอากาศยานนานาชาติอู่ตะเภา', 101.01044, 12.68472, ['u-tapao', 'utapao', 'pattaya', 'rayong', 'อู่ตะเภา']),
  a('KBV', 'Krabi International Airport', 'ท่าอากาศยานนานาชาติกระบี่', 98.98569, 8.09593, ['krabi', 'กระบี่']),
  a('HDY', 'Hat Yai International Airport', 'ท่าอากาศยานนานาชาติหาดใหญ่', 100.39484, 6.93004, ['hat yai', 'hatyai', 'หาดใหญ่']),
  a('CEI', 'Chiang Rai International Airport', 'ท่าอากาศยานเชียงราย', 99.88176, 19.95065, ['chiang rai', 'chiangrai', 'เชียงราย']),
  a('URT', 'Surat Thani Airport', 'ท่าอากาศยานสุราษฎร์ธานี', 99.13973, 9.13316, ['surat thani', 'surat', 'สุราษฎร์ธานี']),
  a('UTH', 'Udon Thani International Airport', 'ท่าอากาศยานนานาชาติอุดรธานี', 102.78758, 17.38567, ['udon thani', 'udon', 'อุดรธานี']),
  a('UBP', 'Ubon Ratchathani Airport', 'ท่าอากาศยานอุบลราชธานี', 104.86922, 15.25302, ['ubon', 'อุบลราชธานี']),
  a('KKC', 'Khon Kaen Airport', 'ท่าอากาศยานขอนแก่น', 102.78342, 16.46622, ['khon kaen', 'ขอนแก่น']),
  a('NST', 'Nakhon Si Thammarat International Airport', 'ท่าอากาศยานนครศรีธรรมราช', 99.94355, 8.53762, ['nakhon si thammarat', 'นครศรีธรรมราช']),
  a('TST', 'Trang Airport', 'ท่าอากาศยานตรัง', 99.61578, 7.50828, ['trang', 'ตรัง']),
  a('PHS', 'Phitsanulok Airport', 'ท่าอากาศยานพิษณุโลก', 100.27792, 16.78155, ['phitsanulok', 'พิษณุโลก']),
  a('NAK', 'Nakhon Ratchasima Airport', 'ท่าอากาศยานนครราชสีมา', 102.30984, 14.94952, ['korat', 'nakhon ratchasima', 'นครราชสีมา', 'โคราช']),
  a('ROI', 'Roi Et Airport', 'ท่าอากาศยานร้อยเอ็ด', 103.77611, 16.11415, ['roi et', 'ร้อยเอ็ด']),
  a('SNO', 'Sakon Nakhon Airport', 'ท่าอากาศยานสกลนคร', 104.1193, 17.19582, ['sakon nakhon', 'สกลนคร']),
  a('KOP', 'Nakhon Phanom Airport', 'ท่าอากาศยานนครพนม', 104.6419, 17.38533, ['nakhon phanom', 'นครพนม']),
  a('LOE', 'Loei Airport', 'ท่าอากาศยานเลย', 101.72497, 17.44429, ['loei', 'เลย']),
  a('BFV', 'Buriram Airport', 'ท่าอากาศยานบุรีรัมย์', 103.25034, 15.22776, ['buriram', 'บุรีรัมย์']),
  a('PXR', 'Surin Airport', 'ท่าอากาศยานสุรินทร์', 103.49762, 14.86755, ['surin', 'สุรินทร์']),
  a('LPT', 'Lampang Airport', 'ท่าอากาศยานลำปาง', 99.50455, 18.26842, ['lampang', 'ลำปาง']),
  a('NNT', 'Nan Airport', 'ท่าอากาศยานน่าน', 100.78446, 18.80762, ['nan', 'น่าน']),
  a('PRH', 'Phrae Airport', 'ท่าอากาศยานแพร่', 100.16421, 18.13111, ['phrae', 'แพร่']),
  a('HGN', 'Mae Hong Son Airport', 'ท่าอากาศยานแม่ฮ่องสอน', 97.97552, 19.30107, ['mae hong son', 'แม่ฮ่องสอน']),
  a('PYY', 'Pai Airport', 'ท่าอากาศยานปาย', 98.43614, 19.37297, ['pai', 'ปาย']),
  a('MAQ', 'Mae Sot Airport', 'ท่าอากาศยานแม่สอด', 98.55073, 16.70011, ['mae sot', 'แม่สอด']),
  a('THS', 'Sukhothai Airport', 'สนามบินสุโขทัย', 99.82097, 17.23527, ['sukhothai', 'สุโขทัย']),
  a('TKT', 'Tak Airport', 'ท่าอากาศยานตาก', 99.25289, 16.89752, ['tak', 'ตาก']),
  a('PHY', 'Phetchabun Airport', 'ท่าอากาศยานเพชรบูรณ์', 101.19051, 16.67586, ['phetchabun', 'เพชรบูรณ์']),
  a('HHQ', 'Hua Hin Airport', 'ท่าอากาศยานหัวหิน', 99.95095, 12.63604, ['hua hin', 'หัวหิน']),
  a('TDX', 'Trat Airport', 'ท่าอากาศยานตราด', 102.32364, 12.2808, ['trat', 'ตราด']),
  a('UNN', 'Ranong Airport', 'ท่าอากาศยานระนอง', 98.58597, 9.77735, ['ranong', 'ระนอง']),
  a('CJM', 'Chumphon Airport', 'ท่าอากาศยานชุมพร', 99.35666, 10.70838, ['chumphon', 'ชุมพร']),
  a('NAW', 'Narathiwat Airport', 'ท่าอากาศยานนราธิวาส', 101.7427, 6.51558, ['narathiwat', 'นราธิวาส']),
  a('PAN', 'Pattani Airport', 'ท่าอากาศยานปัตตานี', 101.15243, 6.78665, ['pattani', 'ปัตตานี']),
  a('BTZ', 'Betong International Airport', 'ท่าอากาศยานนานาชาติเบตง', 101.15058, 5.78944, ['betong', 'เบตง']),
  a('SGZ', 'Songkhla Airport', 'ท่าอากาศยานสงขลา', 100.60759, 7.18632, ['songkhla', 'สงขลา']),
  a('UTR', 'Uttaradit Airport', 'สนามบินอุตรดิตถ์', 100.23349, 17.67357, ['uttaradit', 'อุตรดิตถ์']),
  a('KDT', 'Kamphaeng Saen Airport', 'สนามบินกำแพงแสน', 99.91844, 14.1009, ['kamphaeng saen', 'กำแพงแสน']),
  a('TKH', 'Takhli Airport', 'ท่าอากาศยานนครสวรรค์', 100.29378, 15.27539, ['takhli', 'nakhon sawan', 'ตาคลี']),
  a('KKM', 'Khok Kathiam Airbase', 'ฐานบินโคกกะเทียม', 100.66174, 14.87484, ['khok kathiam', 'lopburi', 'โคกกะเทียม']),
];

/* ------------------------------- matching -------------------------------- */

/** Lowercase, strip accents and punctuation; Thai passes through untouched. */
function norm(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9฀-๿]+/g, '');
}

/**
 * Dice coefficient over character bigrams: how alike two strings are, 0..1.
 *
 * This is what makes a misspelling work. "suvarnabmi" shares eight of its nine
 * bigrams with "suvarnabhumi" and scores 0.80; "suwarphumi", which gets three
 * separate letters wrong, still scores 0.50 - both far above anything a
 * different airport reaches. Bigrams beat edit distance here because they
 * forgive the transpositions and dropped syllables that Thai place names in
 * Latin script attract, and they work unchanged on Thai script.
 */
function similarity(x: string, y: string): number {
  if (x === y) return 1;
  if (x.length < 2 || y.length < 2) return 0;
  const grams = new Map<string, number>();
  for (let i = 0; i < x.length - 1; i++) {
    const g = x.slice(i, i + 2);
    grams.set(g, (grams.get(g) ?? 0) + 1);
  }
  let hits = 0;
  for (let i = 0; i < y.length - 1; i++) {
    const g = y.slice(i, i + 2);
    const n = grams.get(g) ?? 0;
    if (n > 0) {
      grams.set(g, n - 1);
      hits++;
    }
  }
  return (2 * hits) / (x.length - 1 + (y.length - 1));
}

/** Words that mean "I am looking for an airport", in either language. */
const AIRPORT_WORDS = ['airport', 'airfield', 'aerodrome', 'flight', 'สนามบิน', 'ท่าอากาศยาน', 'เที่ยวบิน'];
const TERMINAL_WORDS = ['terminal', 'departure', 'departures', 'arrival', 'arrivals', 'check in', 'checkin', 'อาคารผู้โดยสาร', 'ผู้โดยสาร', 'ขาออก', 'ขาเข้า'];
const PARKING_WORDS = ['park', 'parking', 'carpark', 'car park', 'garage', 'จอดรถ', 'ลานจอด', 'อาคารจอด'];

const MIN_SIMILARITY = 0.42;

/** Best score for one airport against the typed text. */
function scoreAirport(airport: Airport, q: string): number {
  const keys = [
    airport.iata,
    airport.name,
    airport.name.replace(/international|airport/gi, ''),
    airport.nameTh,
    airport.nameTh.replace(/ท่าอากาศยาน|นานาชาติ|สนามบิน/g, ''),
    ...(airport.alias ?? []),
  ]
    .map(norm)
    .filter((k) => k.length > 1);

  let best = 0;
  for (const k of keys) {
    if (k === q) return 1;
    // A short query that opens a name is a strong signal ("suvarn" -> BKK).
    if (q.length >= 3 && k.startsWith(q)) best = Math.max(best, 0.95);
    else if (q.length >= 4 && k.includes(q)) best = Math.max(best, 0.85);
    else best = Math.max(best, similarity(k, q));
  }
  return best;
}

export interface AirportHit {
  airport: Airport;
  feature?: AirportFeature;
  score: number;
}

/**
 * The terminal an airport means when the driver named no terminal at all.
 *
 * Terminal 1 always, where there is more than one. That is the rule the fleet
 * works to: it is the building people mean by "the airport", it is where an
 * unfamiliar passenger is most likely to be dropped, and at Don Mueang it is
 * the international terminal. Guessing by any other rule - array order, nearest
 * to the road, largest - produces a different answer per airport, which is
 * exactly what a driver cannot predict.
 *
 * Explicitly first-in-list is NOT relied on: `features` is authored by hand and
 * a later edit could reorder it without anyone noticing the default moved.
 */
function defaultTerminal(airport: Airport): AirportFeature | undefined {
  const terminals = (airport.features ?? []).filter((f) => f.kind === 'terminal');
  if (!terminals.length) return undefined;
  // "Terminal 1" / "อาคารผู้โดยสาร 1" / key 't1', in that order of confidence.
  const one =
    terminals.find((f) => f.key === 't1') ??
    terminals.find((f) => /\b1\b/.test(f.name) || /\s1$/.test(f.nameTh));
  // Single-terminal airports (Suvarnabhumi's main building) have no "1" to
  // find and simply use the one terminal they have.
  return one ?? terminals[0];
}

/**
 * Find airports (and their terminals / car parks) for a typed query.
 *
 * Returns [] for anything that is not plausibly an airport search, so the
 * ordinary geocoder is left completely alone for ordinary queries.
 */
export function matchAirports(query: string): AirportHit[] {
  const raw = query.trim().toLowerCase();
  if (raw.length < 2) return [];
  const q = norm(raw);
  if (!q) return [];

  const wants = (words: string[]): boolean => words.some((w) => raw.includes(w) || q.includes(norm(w)));
  const genericAirport = wants(AIRPORT_WORDS);
  const wantsTerminal = wants(TERMINAL_WORDS);
  const wantsParking = wants(PARKING_WORDS);

  /*
   * Strip the intent words before scoring the NAME.
   *
   * "suvarnabhumi airport car park" must still score against "suvarnabhumi";
   * left in, the extra words drag the similarity down and the right airport
   * loses to a shorter, wronger name.
   */
  let nameQuery = raw;
  for (const w of [...AIRPORT_WORDS, ...TERMINAL_WORDS, ...PARKING_WORDS]) {
    nameQuery = nameQuery.split(w).join(' ');
  }
  // "floor 5", "level 3", "ชั้น 5" - the floor a driver remembers parking on.
  const floor = raw.match(/(?:floor|level|fl|ชั้น)\s*(\d{1,2})/);
  nameQuery = nameQuery.replace(/(?:floor|level|fl|ชั้น)\s*\d{1,2}/g, ' ');

  /*
   * If nothing survives the stripping, the driver typed the CATEGORY and no
   * name at all ("airport", "สนามบิน") - so nothing should be name-matched.
   *
   * Falling back to the raw query here was subtly wrong, and only visible in
   * Thai: "สนามบิน" is the generic word for airport, but it is also the literal
   * opening of the few Thai names that use it instead of "ท่าอากาศยาน", so
   * Kamphaeng Saen, Sukhothai and Uttaradit scored as exact name matches and
   * pushed the two Bangkok airports - the ones 21 km away - off the list.
   */
  const nq = norm(nameQuery);

  const hits: AirportHit[] = [];
  for (const airport of AIRPORTS) {
    const named = nq.length >= 2 ? scoreAirport(airport, nq) : 0;
    // Either they named this airport, or they asked for airports in general.
    const score = named >= MIN_SIMILARITY ? named : genericAirport ? 0.5 : 0;
    if (score <= 0) continue;

    const namedThis = named >= MIN_SIMILARITY;
    const feats = airport.features ?? [];

    // Sub-features only when this airport was actually named - nobody typing
    // "airport" wants forty car parks.
    if (namedThis && feats.length && (wantsTerminal || wantsParking || floor)) {
      const picked = feats.filter((f) =>
        f.kind === 'terminal' ? wantsTerminal : wantsParking || !!floor,
      );
      // A floor only exists in a multi-storey garage; surface lots cannot match.
      const byFloor = floor
        ? picked.filter((f) => f.kind !== 'parking' || (f.levels ?? 0) >= Number(floor[1]))
        : picked;
      /*
       * "terminal" with no number still means Terminal 1.
       *
       * A specific number is matched by the alias list and scores on its own
       * merit, so "terminal 2" is unaffected - this only breaks the tie when
       * the driver was unspecific, which at Don Mueang otherwise listed T1 and
       * T2 at identical scores and let array order decide.
       */
      const fallbackTerminal = defaultTerminal(airport);
      const numbered = /\b[2-9]\b/.test(raw);

      for (const f of byFloor.length ? byFloor : picked) {
        if (!numbered && f.kind === 'terminal' && fallbackTerminal && f.key === fallbackTerminal.key) {
          hits.push({ airport, feature: f, score: score + 0.08 });
          continue;
        }
        // Multi-storey first among car parks: they are the garages attached to
        // the terminal, they are what "which floor was I on" refers to, and a
        // surface lot in the same breath is a worse answer to the same question.
        hits.push({ airport, feature: f, score: score + (f.levels ? 0.07 : 0.05) });
      }
      // Keep the airport itself available underneath the specific choices.
      hits.push({ airport, score: score - 0.01 });
      continue;
    }

    if (namedThis && feats.length && !wantsTerminal && !wantsParking) {
      /*
       * Named the airport with nothing more specific: lead with the TERMINAL.
       *
       * The comment here used to claim this already happened; it did not. The
       * airport itself was pushed first and the terminal second, so "take me to
       * Suvarnabhumi" routed to `airport.center` - the aerodrome centroid, which
       * for BKK is out among the runways, 1.1 km from the door and reached by
       * service roads. The driver arrived at the back of the airport.
       *
       * `airport.center` is still offered underneath, because it is the right
       * answer when someone means the airport as a place rather than a door.
       * Terminal 1 is what a first terminal is called wherever there is more
       * than one, and it is the international building at Don Mueang.
       */
      const terminal = defaultTerminal(airport);
      if (terminal) hits.push({ airport, feature: terminal, score: score + 0.02 });
      hits.push({ airport, score });
      continue;
    }

    hits.push({ airport, score });
  }

  return hits.sort((x, y) => y.score - x.score);
}

/** Turn matches into Places, nearest first among equally good matches. */
export function airportPlaces(query: string, origin: LngLat | null, limit = 6): Place[] {
  const th = getLang() === 'th';
  const hits = matchAirports(query);
  if (!hits.length) return [];

  const out = hits.slice(0, limit).map(({ airport, feature, score }): Place & { score: number } => {
    const airportName = th ? airport.nameTh || airport.name : airport.name;
    const center = feature ? feature.center : airport.center;
    if (!feature) {
      return {
        id: `air:${airport.iata}`,
        name: `${airportName} (${airport.iata})`,
        address: th ? 'ท่าอากาศยาน' : 'Airport',
        center,
        kind: 'airport',
        distance: origin ? distance(origin, center) : undefined,
        score,
      };
    }
    const featName = th ? feature.nameTh || feature.name : feature.name;
    const note = (th ? feature.noteTh || feature.note : feature.note) ?? '';
    return {
      id: `air:${airport.iata}:${feature.key}`,
      // Airport first, then the part of it: reads as one place on a list that
      // is otherwise full of shops, and never leaves "Terminal 2" ambiguous.
      name: `${airportName} · ${featName}`,
      address: note,
      center,
      kind: feature.kind === 'terminal' ? 'airport-terminal' : 'airport-parking',
      distance: origin ? distance(origin, center) : undefined,
      score,
    };
  });

  // Equal scores (every airport when the query was just "airport") go nearest
  // first, which is the only ordering that means anything to someone driving.
  return out
    .sort((p, q2) =>
      Math.abs(q2.score - p.score) > 0.001
        ? q2.score - p.score
        : (p.distance ?? Infinity) - (q2.distance ?? Infinity),
    )
    .map(({ score: _score, ...place }) => place);
}
